import { z } from 'zod';

import type { LeadOutreachSafetySummary } from '@/types/leads';

export const outreachSafetySummaryRequestSchema = z.object({
  leadIds: z.array(z.string().uuid()).max(100),
}).strict();

export const outreachSafetySummarySchema = z.object({
  lead_id: z.string().uuid(),
  active_suppression_count: z.number().int().nonnegative(),
  primary_suppression_reason: z.enum([
    'do_not_contact',
    'unsubscribe',
    'hard_bounce',
    'spam_complaint',
    'invalid_email',
    'current_customer',
    'existing_customer',
    'open_deal',
    'inbound_dup',
    'manual_block',
    'other',
  ]).nullable(),
  active_suppression_reasons: z.string().nullable(),
  active_suppression_sources: z.string().nullable(),
  latest_suppression_at: z.string().datetime({ offset: true }).nullable(),
}).strict();

export const outreachSafetyMatchSummarySchema = z.object({
  lead_id: z.string().uuid(),
  match_status: z.enum(['blocked', 'needs_review', 'account_expansion_review']).nullable(),
  suppression_reason: outreachSafetySummarySchema.shape.primary_suppression_reason,
  matched_entity_type: z.enum([
    'customer_location',
    'open_deal',
    'blueprint_lead',
    'existing_lead',
  ]).nullable(),
  matched_entity_id: z.string().uuid().nullable(),
  do_not_contact: z.boolean(),
  unresolved_match_count: z.number().int().nonnegative(),
  latest_evaluated_at: z.string().datetime({ offset: true }).nullable(),
  primary_match_id: z.string().uuid().nullable(),
}).strict();

export const outreachSafetySummarySuccessSchema = z.object({
  ok: z.literal(true),
  summaries: z.array(outreachSafetySummarySchema).max(100),
  matchSummaries: z.array(outreachSafetyMatchSummarySchema).max(100),
}).strict();

export function canonicalLeadIds(leadIds: string[]): string[] {
  return [...new Set(leadIds.map((leadId) => leadId.toLowerCase()))];
}

export function parseOutreachSafetySummarySuccess(
  payload: unknown,
  requestedLeadIds: string[],
): {
  summaries: LeadOutreachSafetySummary[];
  matchSummaries: z.infer<typeof outreachSafetyMatchSummarySchema>[];
} {
  const parsed = outreachSafetySummarySuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new Error('Outreach safety summary response is invalid.');
  }

  const requested = new Set(canonicalLeadIds(requestedLeadIds));
  for (const rows of [parsed.data.summaries, parsed.data.matchSummaries]) {
    const seen = new Set<string>();
    for (const summary of rows) {
      const canonicalLeadId = summary.lead_id.toLowerCase();
      if (summary.lead_id !== canonicalLeadId || !requested.has(canonicalLeadId) || seen.has(canonicalLeadId)) {
        throw new Error('Outreach safety summary response is invalid.');
      }
      seen.add(canonicalLeadId);
    }
  }

  return parsed.data;
}
